import { Component, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { Subject, merge, of } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminReward, CardsAdminService, RewardTarget } from '../../../services/admin/cards-admin.service';

@Component({
  selector: 'app-cards-rewards-tab',
  imports: [TranslocoModule, ReactiveFormsModule, DatePipe],
  templateUrl: './cards-rewards-tab.component.html',
  styleUrl: './cards-rewards-tab.component.scss'
})
export class CardsRewardsTabComponent {
  rewards = rxResource({ stream: () => this.cardsAdmin.getRewards$(), defaultValue: [] as AdminReward[] });
  cards = rxResource({ stream: () => this.cardsAdmin.getCards$(), defaultValue: [] });

  targetType = new FormControl<'quest' | 'trip'>('quest', { nonNullable: true });
  search = new FormControl<string>('', { nonNullable: true });
  selectedCardId = new FormControl<number | null>(null);

  targets = signal<RewardTarget[]>([]);
  selectedTarget = signal<RewardTarget | null>(null);

  // Fired after a successful pin so the search pipeline resets exactly like a type change does
  // (see the comment in the constructor for why a silent FormControl.setValue is not enough).
  private searchReset$ = new Subject<void>();

  constructor(
    private cardsAdmin: CardsAdminService,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar
  ) {
    // A type change or a post-pin clear must fully reset the search pipeline, not just the
    // visible FormControl value - three different pieces of state get stale otherwise, all seen
    // in task-W1 review:
    //  1. An in-flight HTTP request for the OLD type/query can resolve after the reset and
    //     repopulate `targets` with the wrong kind of row, which can then be pinned as e.g. a
    //     trip using a quest's id. Fix: every reset re-subscribes via the OUTER switchMap below
    //     and that fresh subscription synchronously emits '' first, which reaches the INNER
    //     switchMap and cancels whatever HTTP call was in flight.
    //  2. debounceTime's pending timer belongs to whichever search$ subscription is currently
    //     active. If a reset only cleared the FormControl value without re-subscribing,
    //     keystrokes typed just BEFORE the reset would still fire after it, tagged with the NEW
    //     type/state once their timer elapsed. Fix: re-subscribing to a fresh
    //     `search.valueChanges.pipe(debounceTime(300))` on every reset (via the outer switchMap)
    //     unsubscribes the old one, which cancels its scheduled emission along with it.
    //  3. distinctUntilChanged's "last seen" pair goes stale too: without a real emission on
    //     reset, retyping the EXACT same text afterwards (same type, right after a pin; or a
    //     different type, same text) compares equal to what was last searched and gets silently
    //     swallowed as a "duplicate" - the admin sees a false "nothing found" for a target that
    //     exists. Fix: the immediate '' from each reset changes the pair distinctUntilChanged
    //     compares against, so the next real query is never mistaken for a repeat.
    const resetTrigger$ = merge(
      this.targetType.valueChanges.pipe(
        tap(() => {
          this.selectedTarget.set(null);
          this.search.setValue('', { emitEvent: false });
        })
      ),
      this.searchReset$
    ).pipe(
      map(() => undefined),
      startWith(undefined)
    );

    resetTrigger$
      .pipe(
        switchMap(() => merge(of(''), this.search.valueChanges.pipe(debounceTime(300)))),
        map((q) => ({ type: this.targetType.value, q })),
        distinctUntilChanged((prev, curr) => prev.type === curr.type && prev.q === curr.q),
        switchMap(({ type, q }) => (q.trim().length < 2 ? of([]) : this.cardsAdmin.searchRewardTargets$(type, q))),
        takeUntilDestroyed()
      )
      .subscribe((rows) => this.targets.set(rows));
  }

  selectTarget(target: RewardTarget): void {
    this.selectedTarget.set(target);
  }

  pin(): void {
    const target = this.selectedTarget();
    const cardId = this.selectedCardId.value;
    if (!target || !cardId) {
      return;
    }
    this.cardsAdmin.pinReward$(this.targetType.value, target.id, cardId).subscribe(() => {
      this.snackBar.open(this.transloco.translate('cardsAdminRewardPinned'), '', { duration: 3000 });
      this.selectedTarget.set(null);
      this.selectedCardId.setValue(null);
      this.search.setValue('', { emitEvent: false });
      this.searchReset$.next();
      this.rewards.reload();
    });
  }

  remove(id: number): void {
    this.cardsAdmin.deleteReward$(id).subscribe(() => this.rewards.reload());
  }
}
